<template>
  <div class="card">
    <div>
      <div class="card-header-block">
        <h4 v-if="$slots.title || title" class="card-title">
          <slot name="title">{{ title }}</slot>
        </h4>
        <p class="card-category">
          <slot name="subTitle">{{ subTitle }}</slot>
        </p>
      </div>

      <div v-if="isLoading" class="loading-message">
        <i class="fas fa-spinner fa-spin"></i> Chargement des données...
      </div>
      <div v-else-if="error" class="error-message">
        <i class="fas fa-exclamation-circle"></i> {{ error }}
      </div>
      <template v-else>
        <!-- Ajout d'une div wrapper avec v-show pour mieux gérer le rendu -->
        <div v-show="!isLoading && !error">
          <div :id="chartId" class="ct-chart"></div>
        </div>
        <div class="footer">
          <h5>Diagramme en barres mis à jour le {{ dernierJour }}</h5>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Chartist from 'chartist';
import 'chartist/dist/chartist.min.css';
import { clearAuthenticatedSession, isAuthenticatedSession } from '@/utils/sessionAuth';

export default {
  name: 'ChartBar',
  props: {
    chartType: { 
      type: String, 
      default: 'Bar' 
    },
    chartOptions: { 
      type: Object, 
      default: () => ({ 
        low: 0,
        axisX: {
          showGrid: false
        },
        axisY: {
          onlyInteger: true,
          offset: 40
        },
        height: '300px'
      }) 
    },
    title: {
      type: String,
      default: 'Statistiques des recommandations'
    },
    subTitle: {
      type: String,
      default: 'Répartition par statut'
    }
  },
  data() {
    return {
      chartId: 'chart-' + Math.random().toString(36).substr(2, 5),
      chartData: { 
        labels: [], 
        series: [] 
      },
      dernierJour: new Date().toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      isLoading: true,
      error: null,
      chartInstance: null // Pour stocker l'instance du graphique
    };
  },
  mounted() {
    this.fetchChartData();
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.detach();
    }
  },
  methods: {
    async fetchChartData() {
      if (!isAuthenticatedSession()) {
        this.error = "Token non trouvé, veuillez vous reconnecter.";
        this.isLoading = false;
        return;
      }

      try {
        const { data } = await axios.get('/api/raci/', {
          withCredentials: true
        });
        
        if (data?.statistiques_globales) {
          this.chartData = {
            labels: ['En cours', 'Terminées', 'En attente', 'Non commencée'],
            series: [[
              data.statistiques_globales.recommandations_en_cours || 0,
              data.statistiques_globales.recommandations_terminees || 0,
              data.statistiques_globales.recommandations_en_attente || 0,
              data.statistiques_globales.recommandations_non_commencee || 0
            ]]
          };
          
          await this.$nextTick(); // Attendre que le DOM soit mis à jour
          this.waitForChartContainerAndRender();
        } else {
          throw new Error('Données manquantes dans la réponse');
        }
      } catch (error) {
        if (error.response?.status === 401) {
          console.error("Session expirée. Veuillez vous reconnecter.");
          return;
        } else {
          this.error = error.response?.data?.detail || "Une erreur est survenue lors du chargement des données.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    async waitForChartContainerAndRender(retry = 0) {
      // On attend que l'élément soit bien dans le DOM avant de rendre le graphique
      const maxRetry = 10;
      const delay = 100; // ms
      const container = document.getElementById(this.chartId);
      if (container) {
        this.renderChart();
      } else if (retry < maxRetry) {
        setTimeout(() => {
          this.waitForChartContainerAndRender(retry + 1);
        }, delay);
      } else {
        this.error = "Impossible d'afficher le graphique (élément non trouvé)";
        console.error('Élément du graphique non trouvé après attente');
      }
    },
    renderChart() {
      try {
        // Vérifier que l'élément existe avant de créer le graphique
        const container = document.getElementById(this.chartId);
        if (!container) {
          throw new Error('Élément du graphique non trouvé');
        }

        // Détruire l'instance précédente si elle existe
        if (this.chartInstance) {
          this.chartInstance.detach();
        }

        this.chartInstance = new Chartist[this.chartType](
          `#${this.chartId}`,
          {
            labels: this.chartData.labels,
            series: this.chartData.series
          },
          {
            ...this.chartOptions,
            // Options de label améliorées
            labelInterpolationFnc: (value) => {
              return `${value}`;
            }
          }
        );

        // Gestion des événements pour les tooltips manuels
        this.setupChartEvents();

      } catch (err) {
        console.error('Erreur rendu graphique', err);
        this.error = 'Erreur lors de l\'affichage du graphique';
        Swal.fire('Erreur', 'Échec du chargement du graphique', 'error');
      }
    },
    setupChartEvents() {
      const container = document.getElementById(this.chartId);
      if (!container) return;

      // Tooltips manuels au survol
      container.querySelectorAll('.ct-bar').forEach(bar => {
        bar.style.cursor = 'pointer';
        bar.addEventListener('mouseenter', (e) => {
          const value = e.target.getAttribute('ct:value');
          if (value) {
            // Création d'un tooltip simple
            const tooltip = document.createElement('div');
            tooltip.className = 'chart-tooltip';
            tooltip.textContent = `${value} recommandations`;
            tooltip.style.position = 'absolute';
            tooltip.style.background = 'rgba(0,0,0,0.7)';
            tooltip.style.color = 'white';
            tooltip.style.padding = '5px 10px';
            tooltip.style.borderRadius = '3px';
            tooltip.style.transform = 'translate(-50%, -100%)';
            
            const rect = e.target.getBoundingClientRect();
            tooltip.style.left = `${rect.left + rect.width / 2}px`;
            tooltip.style.top = `${rect.top}px`;
            
            document.body.appendChild(tooltip);
            e.target._tooltip = tooltip;
          }
        });

        bar.addEventListener('mouseleave', (e) => {
          if (e.target._tooltip) {
            document.body.removeChild(e.target._tooltip);
            delete e.target._tooltip;
          }
        });
      });
    },
    handleApiError(err) {
      console.error('Erreur API:', err);
      
      if (err.response?.status === 401) {
        console.error("Session expirée. Veuillez vous reconnecter.");
        clearAuthenticatedSession();
        // this.$router.push('/connexion'); // Commenté pour éviter la redirection
        return; // Retourner pour empêcher Swal.fire
      } else if (err.response) {
        this.error = `Erreur serveur (${err.response.status})`;
        Swal.fire('Erreur serveur', err.response.statusText, 'error');
      } else if (err.request) {
        this.error = 'Pas de réponse du serveur';
        Swal.fire('Erreur', 'Le serveur ne répond pas', 'error');
      } else {
        this.error = 'Erreur de configuration';
        Swal.fire('Erreur', 'Erreur dans la requête', 'error');
      }
    }
  }
};
</script>

<style scoped>
/* Styles existants améliorés */
.card {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  box-shadow: var(--shadow-md, 0 4px 14px rgba(10, 26, 47, 0.08));
  padding: 20px;
  margin-bottom: 20px;
  position: relative;
  backdrop-filter: blur(10px);
}

.card-header-block {
  margin-bottom: 0.5rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-display), var(--font-primary);
  color: rgba(10, 26, 47, 0.92);
  margin-bottom: 0.5rem;
}

.card-category {
  font-size: 0.9rem;
  color: var(--bank-gray-500);
  margin-bottom: 1.5rem;
}

.ct-chart {
  min-height: 300px;
  position: relative;
}

.loading-message,
.error-message {
  text-align: center;
  padding: 48px 20px;
  font-size: 1rem;
  color: var(--bank-gray-600);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 200px;
}

.loading-message i {
  font-size: 1.5rem;
  color: var(--bank-gold);
}

.error-message {
  color: var(--bank-danger);
}

.error-message i {
  font-size: 1.5rem;
}

.footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(10, 26, 47, 0.1);
}

.footer h5 {
  font-size: 0.85rem;
  color: var(--bank-gray-500);
  font-weight: 500;
  margin: 0;
  text-align: center;
}

/* Styles pour les tooltips (ajoutés dans le DOM) */
.chart-tooltip {
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  font-size: 12px;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .card {
    padding: 16px;
  }
  
  .ct-chart {
    min-height: 250px;
  }
  
  .loading-message,
  .error-message {
    padding: 32px 16px;
    min-height: 160px;
    font-size: 0.95rem;
  }
}
</style>
